import React from 'react';
import { Shield, FileText, Scale, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Page Conditions d'Utilisation
export const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        <button
          data-testid="terms-back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Conditions Générales d'<span className="text-cyan-500">Utilisation</span>
          </h1>
          <p className="text-gray-400">AqmOpti — Dernière mise à jour : Février 2026</p>
        </div>

        <div className="space-y-6 text-gray-300">
          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <p className="leading-relaxed text-sm md:text-base">
              L'accès et l'usage du logiciel AqmOpti (désigné ci-après comme «&nbsp;le Logiciel&nbsp;») impliquent votre adhésion sans réserve aux présentes Conditions Générales d'Utilisation («&nbsp;CGU&nbsp;»). À défaut d'acceptation, vous devez renoncer à toute utilisation du Logiciel.
            </p>
          </section>

          {[
            {
              title: "1. Propriété intellectuelle",
              body: "Le Logiciel et l'ensemble de ses composants (code, scripts, fichiers de configuration, interface, documentation, visuels) demeurent la propriété exclusive d'AqmOpti. Ils bénéficient de la protection prévue par les législations française et internationale en matière de droit d'auteur et de propriété intellectuelle. Aucune reproduction, diffusion, adaptation ou exploitation dérivée n'est autorisée sans le consentement écrit préalable d'AqmOpti."
            },
            {
              title: "2. Licence d'utilisation",
              body: "En contrepartie de votre adhésion aux présentes CGU, AqmOpti vous concède un droit d'usage personnel, restreint, non exclusif, incessible et révocable, à des fins strictement légales et conformes au présent contrat. À ce titre, vous vous interdisez de :",
              list: [
                "céder, louer, sous-licencier, revendre ou transférer à un tiers votre clé de licence ou le Logiciel ;",
                "analyser, désosser, décompiler, désassembler ou reconstituer par quelque moyen que ce soit le code source du Logiciel, hors dérogation expressément autorisée par la loi."
              ]
            },
            {
              title: "3. Conditions d'utilisation",
              body: "L'usage du Logiciel doit s'effectuer dans le respect des lois en vigueur. Toute exploitation à des fins illicites, frauduleuses, malveillantes ou portant préjudice à un tiers est formellement prohibée."
            },
            {
              title: "4. Interdiction du reverse-engineering",
              body: "Toute opération de rétro-ingénierie, de décompilation ou d'analyse interne du Logiciel est strictement prohibée. Le non-respect de cette obligation expose son auteur à des poursuites civiles et/ou pénales en application des articles L335-2 et suivants du Code de la Propriété Intellectuelle."
            },
            {
              title: "5. Détournement d'usage",
              body: "Toute utilisation détournée du Logiciel, notamment via l'altération de licences, l'injection de code malveillant ou l'exploitation de vulnérabilités, donnera lieu à l'engagement de toutes actions civiles et/ou pénales appropriées."
            },
            {
              title: "6. Responsabilité",
              body: "Le Logiciel est mis à disposition «&nbsp;en l'état&nbsp;», sans engagement quant à son adéquation à un usage spécifique. AqmOpti ne pourra en aucun cas être tenu pour responsable de quelque préjudice que ce soit, direct ou indirect, lié à l'utilisation ou à l'impossibilité d'utilisation du Logiciel (notamment perte de données, perte d'exploitation ou autre dommage)."
            },
            {
              title: "7. Évolutions et mises à jour",
              body: "AqmOpti conserve la faculté, à tout instant et sans préavis, de faire évoluer, suspendre, mettre à jour ou cesser tout ou partie du Logiciel. L'utilisateur s'engage à utiliser uniquement les versions officiellement diffusées par AqmOpti."
            },
            {
              title: "8. Durée, suspension et résiliation",
              body: "La licence reste valable jusqu'à sa cessation. AqmOpti peut, à tout moment et sans préavis, résilier, suspendre ou limiter votre accès au Logiciel, notamment en cas de manquement aux présentes CGU ou pour tout motif légitime apprécié par AqmOpti."
            },
            {
              title: "8.1. Mesures disciplinaires",
              body: "AqmOpti se réserve le droit discrétionnaire de :",
              list: [
                "restreindre, suspendre ou supprimer l'accès d'un utilisateur au Logiciel ;",
                "invalider, désactiver ou annuler toute clé de licence ou autorisation antérieurement accordée ;",
                "couper l'accès aux services connexes, y compris au serveur Discord."
              ],
              extra: "Ces décisions peuvent intervenir sans préavis ni justification préalable. Dans la mesure du possible, et hors urgence ou risque sécuritaire, l'utilisateur concerné pourra être informé de la mesure adoptée."
            },
            {
              title: "9. Confidentialité",
              body: "Vous vous engagez à préserver la confidentialité de toute information relative au fonctionnement interne du Logiciel ou à toute donnée propriétaire d'AqmOpti, et à vous abstenir de toute divulgation ou publication sans accord écrit préalable."
            },
            {
              title: "10. Absence de garantie",
              body: "Dans la limite autorisée par la loi, AqmOpti exclut toute garantie, expresse ou implicite, incluant la garantie de qualité marchande ou d'adéquation à un usage particulier. La continuité du service et l'absence de défauts ne sont pas garanties."
            },
            {
              title: "11. Évolution des CGU",
              body: "Les présentes CGU peuvent être modifiées à tout moment par AqmOpti. Les modifications significatives feront l'objet d'une communication aux utilisateurs. La poursuite de l'utilisation du Logiciel après modification implique l'acceptation des nouvelles conditions."
            },
            {
              title: "12. Loi applicable et juridiction",
              body: "Les présentes CGU relèvent du droit français. Tout différend relatif à leur interprétation ou exécution sera porté devant les tribunaux français compétents."
            },
            {
              title: "13. Indépendance des clauses",
              body: "La nullité ou l'inapplicabilité de l'une des clauses des présentes CGU n'affectera pas la validité des autres dispositions, qui resteront pleinement applicables."
            }
          ].map((section, idx) => (
            <section key={idx} className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4">{section.title}</h2>
              <p className="leading-relaxed text-sm md:text-base mb-3" dangerouslySetInnerHTML={{ __html: section.body }} />
              {section.list && (
                <ul className="list-disc list-inside space-y-2 ml-4 text-sm md:text-base">
                  {section.list.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              )}
              {section.extra && <p className="leading-relaxed text-sm md:text-base mt-3">{section.extra}</p>}
            </section>
          ))}

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">Contact</h2>
            <p className="leading-relaxed text-sm md:text-base">
              Pour toute question concernant les présentes CGU, contactez-nous à :{' '}
              <a href="mailto:aqmopti@gmail.com" className="text-cyan-400 hover:text-cyan-300" data-testid="terms-contact-email">
                aqmopti@gmail.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Page Politique de Confidentialité
export const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        <button
          data-testid="privacy-back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Politique de <span className="text-cyan-500">Confidentialité</span>
          </h1>
          <p className="text-gray-400">Dernière mise à jour : Février 2026</p>
        </div>

        <div className="space-y-8 text-gray-300">
          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">1. Introduction</h2>
            <p className="leading-relaxed">
              Chez AqmOpti, nous accordons une grande importance à la protection de votre vie
              privée. Cette politique de confidentialité explique comment nous collectons,
              utilisons, divulguons et protégeons vos informations personnelles lorsque vous
              utilisez nos services d'optimisation informatique.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">2. Informations que nous collectons</h2>
            <p className="leading-relaxed mb-4">Nous pouvons collecter les types d'informations suivants :</p>
            <h3 className="text-lg font-semibold text-white mb-2">2.1 Informations personnelles</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Nom complet</li>
              <li>Adresse e-mail</li>
              <li>Identifiant Discord</li>
              <li>Informations de paiement (traitées par nos prestataires de paiement sécurisés)</li>
              <li>Adresse IP et données de connexion</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">3. Utilisation des informations</h2>
            <p className="leading-relaxed mb-4">Nous utilisons vos informations personnelles pour :</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Fournir et améliorer nos services d'optimisation</li>
              <li>Communiquer avec vous concernant votre commande</li>
              <li>Traiter vos paiements</li>
              <li>Assurer le support après-vente</li>
              <li>Respecter nos obligations légales</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">4. Protection des données</h2>
            <p className="leading-relaxed">
              Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos
              informations personnelles contre tout accès non autorisé, modification, divulgation
              ou destruction.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">5. Partage des informations</h2>
            <p className="leading-relaxed mb-4">
              Nous ne vendons pas vos informations personnelles. Nous pouvons partager vos
              informations uniquement avec :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Nos prestataires de paiement pour traiter vos transactions</li>
              <li>Les autorités légales si la loi l'exige</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">6. Vos droits</h2>
            <p className="leading-relaxed mb-4">Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Droit d'accès à vos données personnelles</li>
              <li>Droit de rectification de vos données</li>
              <li>Droit à l'effacement de vos données</li>
              <li>Droit à la portabilité de vos données</li>
              <li>Droit d'opposition au traitement</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">7. Contact</h2>
            <p className="leading-relaxed">
              Pour toute question concernant cette politique de confidentialité ou pour exercer
              vos droits, vous pouvez nous contacter à :{' '}
              <a
                href="mailto:aqmopti@gmail.com"
                className="text-cyan-400 hover:text-cyan-300"
                data-testid="privacy-contact-email"
              >
                aqmopti@gmail.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Page Mentions Légales
export const LegalNoticePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        <button
          data-testid="legal-back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Scale className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Mentions <span className="text-cyan-500">Légales</span>
          </h1>
          <p className="text-gray-400">Dernière mise à jour : Février 2026</p>
        </div>

        <div className="space-y-8 text-gray-300">
          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">Éditeur du site</h2>
            <ul className="space-y-2">
              <li><strong className="text-white">Nom de l'entreprise :</strong> AqmOpti</li>
              <li><strong className="text-white">Statut :</strong> Auto-entrepreneur</li>
              <li><strong className="text-white">SIREN :</strong> 943 631 771</li>
              <li>
                <strong className="text-white">Email :</strong>{' '}
                <a
                  href="mailto:aqmopti@gmail.com"
                  className="text-cyan-400 hover:text-cyan-300"
                  data-testid="legal-contact-email"
                >
                  aqmopti@gmail.com
                </a>
              </li>
              <li><strong className="text-white">Directeur de la publication :</strong> AqmOpti</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">Hébergeur</h2>
            <ul className="space-y-2">
              <li><strong className="text-white">Nom :</strong> OVHcloud</li>
              <li>
                <strong className="text-white">Adresse :</strong> 2 rue Kellermann, 59100 Roubaix, France
              </li>
              <li>
                <strong className="text-white">Site web :</strong>{' '}
                <a
                  href="https://www.ovhcloud.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300"
                  data-testid="legal-host-link"
                >
                  https://www.ovhcloud.com
                </a>
              </li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">Propriété intellectuelle</h2>
            <p className="leading-relaxed">
              L'ensemble du contenu de ce site (textes, images, logos, vidéos) est protégé par le
              droit d'auteur. Toute reproduction, même partielle, est interdite sans autorisation
              préalable.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">Données personnelles</h2>
            <p className="leading-relaxed">
              Conformément à la loi "Informatique et Libertés" du 6 janvier 1978 modifiée et au
              Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit
              d'accès, de rectification et de suppression des données vous concernant. Pour
              exercer ce droit, contactez-nous à :{' '}
              <a
                href="mailto:aqmopti@gmail.com"
                className="text-cyan-400 hover:text-cyan-300"
                data-testid="legal-rgpd-email"
              >
                aqmopti@gmail.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Page Conditions Générales de Vente (CGV)
export const SalesTermsPage = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Objet",
      body: "Les présentes CGV régissent la vente de licences d'utilisation du logiciel AqmOpti (le «&nbsp;Logiciel&nbsp;») par AqmOpti (auto-entrepreneur) à toute personne physique passant commande (le «&nbsp;Client&nbsp;»)."
    },
    {
      title: "2. Caractéristiques et prix",
      body: "Les licences sont décrites sur le site préalablement à l'achat. Les prix sont affichés en euros toutes taxes comprises (TTC). En sa qualité d'auto-entrepreneur, AqmOpti relève de la franchise en base de TVA prévue à l'article 293 B du CGI : «&nbsp;TVA non applicable, art. 293 B du CGI&nbsp;». Les tarifs applicables sont ceux affichés au moment de la validation de la commande."
    },
    {
      title: "3. Commande",
      body: "La commande est réputée validée après sélection de la licence, saisie de l'identifiant Discord du Client, acceptation des présentes CGV et confirmation du paiement. Une fois validée, la commande engage fermement et définitivement le Client."
    },
    {
      title: "4. Paiement",
      body: "Le paiement s'effectue via Stripe (carte bancaire) ou PayPal. AqmOpti ne conserve aucune donnée bancaire. Tout refus de paiement par l'organisme bancaire entraîne l'annulation automatique de la commande."
    },
    {
      title: "5. Livraison",
      body: "La livraison s'effectue immédiatement et exclusivement par voie dématérialisée. Dès validation du paiement, une licence est générée et associée à l'identifiant Discord du Client. Celui-ci peut à tout moment récupérer sa clé via la commande /cle sur le serveur Discord officiel. Aucun envoi physique n'est prévu."
    },
    {
      title: "6. Droit de rétractation",
      body: "Conformément à l'article L221-28 13° du Code de la Consommation, le droit de rétractation de 14 jours n'est pas applicable aux contrats de fourniture d'un contenu numérique non délivré sur support matériel dont l'exécution a débuté après l'accord exprès du Client. En validant sa commande, le Client renonce expressément à ce droit dès la mise à disposition de sa licence. Aucune demande de remboursement ne sera donc recevable après récupération de la clé."
    },
    {
      title: "7. Remboursement exceptionnel",
      body: "Un remboursement peut être consenti, à la seule appréciation d'AqmOpti, en cas de défaillance technique majeure rendant le Logiciel inutilisable, de double prélèvement, ou de défaut de livraison constaté au-delà de 72 heures. La demande doit être formulée via un ticket sur le Discord officiel, accompagnée d'une preuve d'achat."
    },
    {
      title: "8. Licence et restrictions",
      body: "L'achat ouvre au Client un droit d'usage personnel, non exclusif, incessible et non transférable du Logiciel, dans la limite du nombre d'utilisations acquis. Tout manquement aux conditions de licence (partage, rétro-ingénierie, redistribution) entraîne la résiliation immédiate sans remboursement et peut donner lieu aux poursuites prévues par les articles L335-2 et suivants du Code de la Propriété Intellectuelle."
    },
    {
      title: "9. Garanties légales",
      body: "AqmOpti est tenu des garanties légales de conformité (articles L217-3 et suivants du Code de la Consommation) et de la garantie des vices cachés (articles 1641 et suivants du Code Civil). Ces garanties couvrent les défauts de conformité du Logiciel par rapport à sa description."
    },
    {
      title: "10. Limitation de responsabilité",
      body: "La responsabilité d'AqmOpti, en cas de manquement à ses obligations, ne pourra excéder le montant effectivement réglé par le Client au titre de sa licence. AqmOpti ne pourra être tenu responsable des préjudices indirects, pertes de données ou tout dommage consécutif à l'usage du Logiciel."
    },
    {
      title: "11. Support",
      body: "L'assistance technique et commerciale s'effectue uniquement par le système de tickets du serveur Discord officiel. Aucune autre voie (téléphone, email direct, message privé Discord) n'est prise en charge."
    },
    {
      title: "12. Protection des données",
      body: "Le traitement des données personnelles est encadré par les Mentions Légales du site, en conformité avec le RGPD. Le Client peut exercer ses droits d'accès, de rectification, d'effacement et d'opposition à l'adresse aqmopti@gmail.com."
    },
    {
      title: "13. Force majeure",
      body: "AqmOpti ne peut être tenu pour responsable en cas d'événement de force majeure ou de circonstance échappant à son contrôle (interruption d'hébergement, indisponibilité de Discord, cyberattaque, etc.) entravant temporairement la fourniture du service."
    },
    {
      title: "14. Règlement des litiges",
      body: "Le Client est invité à privilégier la résolution amiable de tout litige via un ticket Discord. Conformément à l'article L612-1 du Code de la Consommation, il peut également recourir gratuitement à un médiateur de la consommation. À défaut d'accord amiable, les tribunaux français sont seuls compétents."
    },
    {
      title: "15. Modification des CGV",
      body: "AqmOpti se réserve le droit de modifier les présentes CGV à tout moment. La version opposable est celle en vigueur au jour de la validation de la commande."
    },
    {
      title: "16. Indépendance des clauses",
      body: "L'invalidité ou l'inapplicabilité d'une clause des présentes CGV n'entraîne pas celle des autres dispositions, qui demeurent pleinement applicables."
    }
  ];

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        <button
          data-testid="cgv-back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Scale className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Conditions Générales de <span className="text-cyan-500">Vente</span>
          </h1>
          <p className="text-gray-400">AqmOpti — Dernière mise à jour : Février 2026</p>
        </div>

        <div className="space-y-6 text-gray-300">
          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <p className="leading-relaxed text-sm md:text-base">
              Toute commande passée sur le site aqmoptimisation.fr emporte adhésion sans réserve aux présentes Conditions Générales de Vente («&nbsp;CGV&nbsp;»). À défaut d'acceptation, le Client doit s'abstenir de toute commande.
            </p>
          </section>

          {sections.map((section, idx) => (
            <section key={idx} className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4">{section.title}</h2>
              <p className="leading-relaxed text-sm md:text-base" dangerouslySetInnerHTML={{ __html: section.body }} />
            </section>
          ))}

          <section className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">Contact</h2>
            <p className="leading-relaxed text-sm md:text-base">
              Pour toute question concernant les présentes CGV, contactez-nous à :{' '}
              <a href="mailto:aqmopti@gmail.com" className="text-cyan-400 hover:text-cyan-300" data-testid="cgv-contact-email">
                aqmopti@gmail.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

